import { useEffect, useState } from "react";
import { toast } from "react-toastify";

import { Switch } from "antd";
import { ChartProduct, InjectionProduct, SalesProduct } from "constants/chart.constants";
import styled from "styled-components";
import { getProductIcon } from "utils/chart/getProductIcon";
import { getShortenedProductName } from "utils/chart/productNameHelpers";

import { BaseButton } from "components/base";
import ProductHeader from "components/charts/components/ProductHeader";
import { productTabs } from "components/multiphase-chart/constants/shared.constants";
import { useChartSettings } from "components/multiphase-chart/context";
import { addProduct, setProducts } from "components/multiphase-chart/context/reducer";
import { useAxisUnitLimit } from "components/multiphase-chart/hooks";
import { getCurrentSeriesStyleByProductLabel } from "components/multiphase-chart/util/productStyles";

import ProductStyleSelector from "./ProductStyleSelector";
import { getProductButtonClass } from "./utils/getProductButtonClass";

export interface IProducts {
  tab: string;
  isPrimary: boolean;
}

/**
 * Break a Wellhead name after the first word, so "Wellhead BOE 6:1" reads
 * "Wellhead" over "BOE 6:1" and every name here is two lines.
 *
 * Not the shared breakBeforeRatio, which is right for the wider selectors but
 * gives three lines in this grid: the tiles are four to a row, so the break it
 * puts before the ratio lands under a "Wellhead BOE" that has already wrapped
 * on width.
 */
const breakAfterWellhead = (label: string): string =>
  label?.startsWith("Wellhead ") ? label.replace(" ", "\n") : label;

const Products = ({ tab, isPrimary }: IProducts) => {
  const [chartSettings, chartSettingsDispatch] = useChartSettings();

  // Initialize productList with useState
  const [productList, setProductList] = useState([]);
  const { canAddProduct } = useAxisUnitLimit(chartSettings.currentPreset.products);
  const chartType = chartSettings?.currentPreset?.chartType;

  // Use useEffect to update productList based on the value of tab
  useEffect(() => {
    switch (tab) {
      case productTabs.Production:
        setProductList(ChartProduct);
        break;
      case productTabs.Sales:
        setProductList(SalesProduct);
        break;
      case productTabs.Injection:
        setProductList(InjectionProduct);
        break;
      default:
        setProductList([]);
        break;
    }
  }, [tab, chartType]);

  const { currentPreset, isViewMode, currentTab } = chartSettings;

  const isPrimaryContaining = (prodName: string) => {
    return (
      currentPreset.products.filter(
        (p: { axis: string; name: string }) => p.axis === "primary" && p.name === prodName
      ).length > 0
    );
  };

  const isSecondaryContaining = (
    prodName: string // for newPreset
  ) =>
    currentPreset.products.filter(
      (p: { axis: string; name: string }) => p.axis === "secondary" && p.name === prodName
    ).length > 0;

  const [yieldsType, setYieldsType] = useState("Raw");

  const onChangeType = () => {
    if (yieldsType === "Raw") {
      setYieldsType("Sales");
    } else {
      setYieldsType("Raw");
    }
  };

  return (
    <ProductsContainer>
      {productList
        .filter(
          (p, idx) =>
            (yieldsType === "Raw" && idx != 3) || (yieldsType === "Sales" && idx != 2)
        )
        .map((header) => {
          return (
            <>
              {tab == productTabs.Sales && header.name?.includes("Yields") && (
                <ButtonContainer>
                  <Switch
                    unCheckedChildren={"Raw"}
                    checkedChildren={"Sales"}
                    checked={yieldsType === "Sales"}
                    onChange={onChangeType}
                    size="small"></Switch>
                </ButtonContainer>
              )}
              <ProductHeader key={header.name} header={header.name}>
                {header.items.map((prod) => {
                  const isPrimaryContainingProduct = isPrimaryContaining(prod);
                  const isSecondaryContainingProduct = isSecondaryContaining(prod);

                  const isProductColorSelectorHidden = isViewMode
                    ? true
                    : !isPrimaryContainingProduct && !isSecondaryContainingProduct;

                  const currentStyleColor = getCurrentSeriesStyleByProductLabel(
                    prod,
                    currentPreset?.style
                  ).color;

                  return (
                    <ProductButton
                      svgcolor={currentStyleColor}
                      key={prod}
                      ellipses={false}
                      onClick={() => {
                        if (!isViewMode) {
                          const productObj = {
                            name: prod,
                            axis: isPrimary ? "primary" : "secondary"
                          };
                          if (isPrimary) {
                            if (!isPrimaryContaining(prod)) {
                              if (!isSecondaryContaining(prod)) {
                                if (canAddProduct(prod, "primary")) {
                                  addProduct(chartSettingsDispatch, {
                                    name: prod,
                                    axis: "primary"
                                  });
                                } else {
                                  toast.warning(
                                    `Maximum unit types reached for left axis.
                                    `,
                                    {
                                      containerId: "presets-popover-toast",
                                      position: "top-right"
                                    }
                                  );
                                }
                              }
                            } else {
                              const filteredProducts = currentPreset.products.filter(
                                (a) => JSON.stringify(a) !== JSON.stringify(productObj)
                              );

                              setProducts(chartSettingsDispatch, filteredProducts);
                            }
                          } else {
                            // isSecondary
                            if (!isSecondaryContaining(prod)) {
                              if (!isPrimaryContaining(prod)) {
                                if (canAddProduct(prod, "secondary")) {
                                  addProduct(chartSettingsDispatch, {
                                    name: prod,
                                    axis: "secondary"
                                  });
                                } else {
                                  toast.warning(
                                    `Maximum unit types reached for right axis.
                                    `,
                                    {
                                      containerId: "presets-popover-toast",
                                      position: "top-right"
                                    }
                                  );
                                }
                              }
                            } else {
                              const filteredProducts = currentPreset.products.filter(
                                (a) => JSON.stringify(a) !== JSON.stringify(productObj)
                              );

                              setProducts(chartSettingsDispatch, filteredProducts);
                            }
                          }
                        }
                      }}
                      appearance=""
                      className={getProductButtonClass({
                        isViewMode,
                        isPrimary,
                        chartType,
                        prod,
                        isPrimaryContainingProduct,
                        isSecondaryContainingProduct,
                        canAddProduct
                      })}>
                      {isPrimaryContainingProduct || isSecondaryContainingProduct ? (
                        <PrimarySecondaryIndicator
                          isactive={
                            (currentTab === "Primary" && isPrimaryContainingProduct) ||
                            (currentTab === "Secondary" && isSecondaryContainingProduct)
                          }>
                          {isPrimaryContainingProduct ? "P" : "S"}
                        </PrimarySecondaryIndicator>
                      ) : null}
                      {getProductIcon(prod)}
                      <div>
                        {breakAfterWellhead(
                          getShortenedProductName(prod, "Raw", "Sales")
                        )}
                      </div>
                      <ProductStyleSelector
                        isHidden={isProductColorSelectorHidden}
                        productLabel={prod}
                      />
                    </ProductButton>
                  );
                })}
              </ProductHeader>
            </>
          );
        })}
    </ProductsContainer>
  );
};

export default Products;

const ProductsContainer = styled.div`
  position: relative;

  /* Style for toast container to ensure it appears within the component */
  .Toastify__toast-container {
    width: 90%;
    max-width: 350px;
  }
`;

const ProductButton = styled(BaseButton)`
  position: relative;
  display: inline-block;
  /* A minimum rather than a fixed height: the BOE names run to two lines. */
  min-height: 90px;
  /* Narrower than BaseButton's 0 15px. Four tiles to a row makes each one
     79-83px wide, depending on whether the pane is showing its scrollbar, and
     30px of side padding leaves under 53px of content. "BOE 20:1" does not fit
     that, so it wraps again under the break above it and the tile runs to three
     lines. 4px leaves 71px, which the whole family fits. */
  padding: 0 4px;
  border-radius: 4px;
  color: #041c2c;
  background-color: unset;
  font-weight: var(--fontWeightMedium);
  font-size: 12px;

  &.isSelected {
    cursor: pointer;
    background: #d8eeee;
    color: #041c2c;
    svg {
      color: ${(props) => props.svgcolor || "#a2aaad"};
    }
  }

  span {
    margin-top: 0;
    /* pre-line, so the line break the BOE label carries is rendered rather than
       collapsed into a space. Without it the names wrap on width alone, which
       breaks the family in different places. */
    white-space: pre-line;
  }
  .label {
    line-height: 18px;
    margin: 0 2px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
  }

  &.isSelected:hover {
    cursor: pointer;

    background: #b8e0e0;
    svg {
      color: ${(props) => props.svgcolor || "#a2aaad"};
    }
  }

  svg {
    min-height: 40px;
    color: #a2aaad;
    transition: color var(--duration-short);
  }

  &.isAlreadyChosen {
    cursor: default;
    background: rgba(var(--color-shadow-rgb), 0.2);
    color: black;
    svg {
      color: ${(props) => props.svgcolor || "#a2aaad"};
    }
  }
  &.notSelectable {
    cursor: not-allowed;
    background: transparent;
    opacity: 0.6;
    svg {
      color: #a2aaad;
      opacity: 0.6;
    }
  }

  &.isAlreadyChosen:hover {
    cursor: default;
  }

  &.isSelectableViewMode:hover {
    cursor: cursor;
    background: rgba(var(--color-shadow-rgb), 0.3);
    svg {
      color: var(--color-primary);
    }
  }

  &.isViewMode:hover {
    cursor: default;
    background: none;
    svg {
      color: #a2aaad;
    }
  }

  &.isSelectedViewMode {
    cursor: default;
    background: #d8eeee;
    color: #041c2c;
    svg {
      color: ${(props) => props.svgcolor || "#a2aaad"};
    }
  }

  &.isSelectedViewMode:hover {
    cursor: default;
    background: #d8eeee;
    svg {
      color: ${(props) => props.svgcolor || "#a2aaad"};
    }
  }

  caret-color: transparent;
`;

const PrimarySecondaryIndicator = styled.div`
  position: absolute;
  top: -1px;
  left: 44px;
  width: 15px;
  height: 15px;
  background-color: ${(props) => (props.isactive ? "#50b1a5" : "#a2aaad")};
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 10px;
  color: #f8f8f8;
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  transform: translate(-10px, 30px);
  margin-top: -16px;
  position: relative;
  z-index: 1;

  .ant-switch {
    background-color: var(--color-primary);
  }
`;
